import { useState } from "react";
import type { Approval } from "../api/types";
import { useApproval } from "../hooks/useApproval";

interface Props {
  postId: string;
  approval: Approval;
  canPublish: boolean;  // true iff R2 + IG keys are configured
  isVideo: boolean;
}

export function ApprovalPanel({ postId, approval, canPublish, isVideo }: Props) {
  const { approve, reject } = useApproval(postId);
  const [notes, setNotes] = useState(approval.notes ?? "");
  const [confirmPublish, setConfirmPublish] = useState(false);

  const disabled = approve.isPending || reject.isPending;
  const statusColor =
    approval.status === "published"
      ? "bg-emerald-100 text-emerald-900 ring-emerald-300"
      : approval.status === "approved"
        ? "bg-sky-100 text-sky-900 ring-sky-300"
        : approval.status === "rejected"
          ? "bg-red-100 text-red-900 ring-red-300"
          : "bg-amber-100 text-amber-900 ring-amber-300";

  return (
    <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-black/5">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-brand-text-dark/60">
          Approval
        </h3>
        <span
          className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider ring-1 ${statusColor}`}
        >
          {approval.status}
        </span>
      </div>

      {approval.instagram_url ? (
        <a
          href={approval.instagram_url}
          target="_blank"
          rel="noreferrer"
          className="mt-3 block truncate text-xs text-sky-700 hover:underline"
        >
          {approval.instagram_url}
        </a>
      ) : null}

      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Notizen zum Approval/Reject (optional)"
        rows={3}
        className="mt-4 w-full resize-y rounded-lg border border-black/10 bg-white px-3 py-2 text-sm focus:border-brand-blue focus:outline-none"
      />

      {isVideo && canPublish ? (
        <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-xl bg-brand-beige px-3 py-2.5 text-sm">
          <input
            type="checkbox"
            checked={confirmPublish}
            onChange={(e) => setConfirmPublish(e.target.checked)}
            className="mt-1 h-4 w-4"
          />
          <div>
            <div className="font-bold text-brand-text-dark">
              Direkt auf Instagram veröffentlichen
            </div>
            <div className="text-xs text-brand-text-dark/60">
              R2-Upload + Graph API REELS publish (irreversibel)
            </div>
          </div>
        </label>
      ) : null}

      <div className="mt-4 flex gap-2">
        <button
          type="button"
          disabled={disabled}
          onClick={() =>
            approve.mutate({
              notes: notes.trim() || undefined,
              publish_to_instagram: confirmPublish,
            })
          }
          className="flex-1 rounded-full bg-emerald-600 px-4 py-2.5 text-sm font-bold uppercase tracking-wider text-white shadow-lg shadow-emerald-600/20 transition hover:bg-emerald-700 disabled:opacity-50"
        >
          {confirmPublish ? "Approve & Publish" : "Approve"}
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => reject.mutate({ notes: notes.trim() || undefined })}
          className="flex-1 rounded-full bg-red-600 px-4 py-2.5 text-sm font-bold uppercase tracking-wider text-white shadow-lg shadow-red-600/20 transition hover:bg-red-700 disabled:opacity-50"
        >
          Reject
        </button>
      </div>

      {(approve.error || reject.error) && (
        <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-900">
          {String(approve.error ?? reject.error)}
        </div>
      )}
    </div>
  );
}
